<template>
    <Modal
        :is-open="showTripModal"
        :title="modalTitle"
        :errors="errors"
        @close="closeModal()"
    >
        <template #content>
            <Input
                label="Name"
                name="title"
                id="trip-name"
                placeholder="Name your trip"
                v-model:inputValue="title"
                class="mb-4"
                :errors="tripFormErrors"
                @onFocus="clearError('title')"
            />
            <Input
                id="trip-destination"
                name="country"
                label="Destination"
                placeholder="Select destination"
                :errors="tripFormErrors"
                class="mb-4"
            >
                <template #input>
                    <Suspense>
                        <PlacesAutocomplete
                            :types="['(regions)']"
                            id="trip-destination"
                            placeholder="Select destination"
                            @placeChanged="placeChanged"
                            @placeCleared="placeCleared"
                            @onFocus="clearError('country')"
                            :error="destinationError"
                            :currentPlace="tripDestination"
                            :class="{
                                '!border-red-500': destinationError,
                            }"
                        />
                    </Suspense>
                </template>
                <template #error>
                    <div
                        class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                        role="alert"
                        v-if="destinationError"
                    >
                        <div class="flex items-center justify-center">
                            <Icon icon="caution" />
                        </div>
                        Please enter a destination
                    </div>
                </template>
            </Input>
            <div class="w-full flex gap-4 mb-4">
                <Input
                    id="check-in-date"
                    name="start_date"
                    label="Dates"
                    placeholder="Check In"
                    :options="{
                        icon: 'calendar',
                        iconPosition: 'right',
                        wrapperClass: 'flex-1',
                    }"
                    :errors="tripFormErrors"
                >
                    <template v-slot:input>
                        <Flatpickr
                            :config="dateStartConfig"
                            v-model="tripsStore.activeTrip.start_date"
                            name="start_date"
                            id="start-date"
                            class="relative block w-full rounded border border-slate-200 bg-white py-1.5 pr-10 text-gray-900 placeholder:text-slate-500 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                            placeholder="Start"
                            :class="{
                                '!border-red-500': tripFormErrors.start_date,
                            }"
                            @on-change="onStartChange"
                            @focus="clearError('start_date')"
                        />
                    </template>
                </Input>
                <Input
                    id="check-in-date"
                    name="end_date"
                    label="End date"
                    placeholder="Check In"
                    :options="{
                        icon: 'calendar',
                        hideLabel: true,
                        iconPosition: 'right',
                        fakeLabelSpace: true,
                        wrapperClass: 'flex-1',
                    }"
                    :errors="tripFormErrors"
                >
                    <template v-slot:input>
                        <Flatpickr
                            :config="dateEndConfig"
                            v-model="tripsStore.activeTrip.end_date"
                            name="end_date"
                            id="end-date"
                            class="relative block w-full rounded border border-slate-200 bg-white py-1.5 pr-10 text-gray-900 placeholder:text-slate-500 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                            placeholder="End"
                            :class="{
                                '!border-red-500': tripFormErrors.end_date,
                            }"
                            @on-change="onEndChange"
                            @focus="clearError('end_date')"
                        />
                    </template>
                </Input>
            </div>
            <Textarea
                label="Description"
                name="description"
                id="trip-description"
                class="mb-4"
                placeholder="Write a short description"
                v-model:inputValue="description"
                :errors="tripFormErrors"
                :options="{
                    optional: true,
                }"
                @onFocus="clearError('description')"
            />
            <Input
                label="Cover"
                name="cover"
                id="trip-cover"
                placeholder="Upload or select cover image"
                class="mb-4"
                :errors="tripFormErrors"
                @onFocus="clearError('country')"
            >
                <template #input>
                    <TripImageUpload
                        v-bind:query="tripDestination.valueForSearch"
                        :image="tripImage"
                        :trip="activeTrip"
                        @save="updateCoverImage"
                    />
                </template>
            </Input>
        </template>
        <template #errors>
            <div class="flex justify-center p-1" v-if="errors.length > 0">
                <Errors :errors="errors" />
            </div>
        </template>
        <template #footer>
            <div class="flex justify-end items-center">
                <div class="flex gap-4">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        @click="closeModal"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        @click="handleTripAction"
                    >
                        {{ btnAction }}
                        <span class="inline ml-2" v-if="tripsStore.loading">
                            <i class="fas fa-spinner-third fa-spin"></i>
                        </span>
                    </button>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { ref, computed, watch } from 'vue';
import { useTripsStore } from '@/store/trips';
import Flatpickr from 'vue-flatpickr-component';
import Modal from '@/components/ui/modal/ui-modal.vue';
import Input from '@/components/ui/inputs/ui-input.vue';
import Textarea from '@/components/ui/inputs/ui-textarea.vue';
import PlacesAutocomplete from '@/components/ui/places-autocomplete/places-autocomplete.vue';
import TripImageUpload from '@/components/trips/trip-image-upload.vue';
import Icon from '@/components/ui/icon/icon.vue';
import Errors from '@/components/errors.vue';

const tripsStore = useTripsStore();
const { showTripModal, activeTrip, tripFormErrors, errors } =
    storeToRefs(tripsStore);

const modalTitle = computed(() => {
    return tripsStore.activeTrip.id ? 'Edit trip' : 'Create new trip';
});

const btnAction = computed(() => {
    return tripsStore.activeTrip.id ? 'Save' : 'Create';
});

const title = computed({
    get() {
        return activeTrip.value.title;
    },
    set(value) {
        tripsStore.setActiveTrip({ ...activeTrip.value, title: value });
    },
});

const cdnUrl = computed(() => {
    return window.cdnUrl;
});

const tripImage = computed(() => {
    if (!activeTrip.value?.image) return null;
    if (
        typeof activeTrip.value.image === 'string' &&
        activeTrip.value.image.startsWith('http')
    ) {
        return activeTrip.value.image;
    }
    return `${cdnUrl.value}/${activeTrip.value.image.path}/${activeTrip.value.image.file_name}`;
});

const description = computed({
    get() {
        return activeTrip.value.description;
    },
    set(value) {
        tripsStore.setActiveTrip({ ...activeTrip.value, description: value });
    },
});

const tripDestination = computed({
    get() {
        const city = activeTrip.value.city || '';
        let state = activeTrip.value.state || '';
        if (state) {
            state = ' ' + state;
        }
        const country = activeTrip.value.country_short
            ? activeTrip.value.country_short
            : activeTrip.value.country || '';

        let formattedAddress = '';
        if ((state || city) && country) {
            formattedAddress = `${city}${state}, ${country}`;
        } else if (state || city) {
            formattedAddress = state || city;
        } else if (!state && !city) {
            formattedAddress = activeTrip.value.country;
        }

        return {
            formattedAddress,
            city,
            state,
            country,
            valueForSearch:
                `${city?.trim()}+${state?.trim()}+${country?.trim()}`
                    .replace(/,/g, '')
                    .replace(/ /g, '+')
                    .replace(/^\+/, '')
                    .replace(/\+$/, '')
                    .replace(/\+\+/, '+')
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, ''),
        };
    },
    set(value) {
        tripsStore.setActiveTrip({ ...activeTrip.value, destination: value });
    },
});

const destinationError = ref(false);

const startDateError = computed(() => {
    return tripFormErrors.hasOwnProperty('start_date') &&
        tripFormErrors.value.start_date
        ? true
        : false;
});

const endDateError = computed(() => {
    return tripFormErrors.hasOwnProperty('end_date') &&
        tripFormErrors.value.end_date
        ? true
        : false;
});

const dateStartConfig = ref({
    dateFormat: 'm/d/Y',
    disableMobile: true,
});

const dateEndConfig = ref({
    dateFormat: 'm/d/Y',
    disableMobile: true,
});

const hasSelectedValidPlace = ref(false);

const validate = () => {
    tripsStore.clearErrors();

    if (!activeTrip.value.title) {
        tripsStore.setError({ message: 'Please enter a trip name' });
        tripsStore.setFormError('title', 'Please enter a trip name');
    }

    if (!activeTrip.value.country) {
        tripsStore.setError({ message: 'Please select a destination' });
        tripsStore.setFormError('country', 'Please select a destination');
    } else if (!hasSelectedValidPlace.value) {
        tripsStore.setError({ message: 'Please select a destination from the dropdown options' });
        tripsStore.setFormError('country', 'Please select a destination from the dropdown options');
    }

    if (!activeTrip.value.start_date) {
        tripsStore.setError({ message: 'Please enter a start date' });
        tripsStore.setFormError('start_date', 'Please enter a start date');
    }

    if (!activeTrip.value.end_date) {
        tripsStore.setError({ message: 'Please enter an end date' });
        tripsStore.setFormError('end_date', 'Please enter an end date');
    }
    if (
        !tripDestination.value.hasOwnProperty('country') ||
        !tripDestination.value.country
    ) {
        destinationError.value = true;
        tripsStore.setError({ message: 'Please enter a destination' });
        tripsStore.setFormError('destination', 'Please enter a destination');
    }
};

const handleTripAction = () => {
    validate();

    if (!errors.value.length) {
        const action = tripsStore.activeTrip.id
            ? tripsStore.updateTrip()
            : tripsStore.createTrip();
        action.then(() => {
            closeModal();
        });
    }
};

const placeChanged = (data) => {
    tripDestination.value = data;
    tripsStore.setPlace(data);
    hasSelectedValidPlace.value = true;
};

const placeCleared = () => {
    tripDestination.value = null;
    tripsStore.clearPlace();
    hasSelectedValidPlace.value = false;
};

const closeModal = () => {
    tripsStore.setShowTripModal(false);
    tripsStore.clearErrors();
    dateStartConfig.value.maxDate = null;
    dateEndConfig.value.minDate = null;
    if (activeTrip.value.uuid) tripsStore.getTrip(activeTrip.value.uuid);
};

const onStartChange = (selectedDates, dateStr, instance) => {
    dateEndConfig.value.minDate = dateStr;
};

const onEndChange = (selectedDates, dateStr, instance) => {
    dateStartConfig.value.maxDate = dateStr;
};

const updateCoverImage = (imageUrl) => {
    tripsStore.activeTrip.image = imageUrl;
};

watch(
    () => showTripModal.value,
    (newValue) => {
        if (!newValue) {
            tripsStore.clearErrors();
        } else {
            hasSelectedValidPlace.value = activeTrip.value.id && activeTrip.value.country ? true : false;
        }
    }
);

const clearError = (field) => {
    destinationError.value = false;
    if (tripFormErrors.value[field]) {
        delete tripFormErrors.value[field];
    }
};
</script>
